import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis } from 'apis/pilotAPI';
import { StudioModal } from './StudioModal';
import styles from './StudioModal.module.scss';
import { AWS_REGION_OPTIONS, ENVIRONMENT_TYPE_OPTIONS } from './environmentFormConstants';

const awsRegionEnumValues = AWS_REGION_OPTIONS.map((o) => o.value) as [string, ...string[]];
const ENV_TYPES_REQUIRING_UTILITY_CREATE = new Set(['dev', 'uat']);

function extractUtilityIdFromCreateUtilityResponse(payload: any): number | null {
  const candidates = [
    payload?.data?.id,
    payload?.data?.data?.id,
    payload?.data?.utility?.id,
    payload?.id,
    payload?.utility?.id,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

const createEnvironmentSchema = z
  .object({
    name: z.string().min(1, 'Name is required.').max(100, 'Name must be 100 characters or less.'),
    awsRegion: z.enum(awsRegionEnumValues, { required_error: 'AWS Region is required.' }),

    // Required
    serverUrl: z.string().url('Invalid URL format.').min(1, 'Server URL is required.'),
    type: z.enum(ENVIRONMENT_TYPE_OPTIONS, { required_error: 'Type is required.' }),

    // Utility is required only for dev/uat (we will create + resolve utility_id)
    utilityName: z.string().optional(),

    // Optional
    // allow empty string (optional) OR a valid URL
    frontend_url: z.union([z.string().url('Invalid URL format.'), z.literal('')]),
    stackName: z.string().max(50, 'Stack Name must be 50 characters or less.').optional(),
    stateAlphaCode: z
      .string()
      .max(3, 'State Alpha Code must be 2-3 characters.')
      .optional()
      .refine((v) => !v || v.length >= 2, 'State Alpha Code must be 2-3 characters.'),
  })
  .superRefine((v, ctx) => {
    // If type is dev/uat, stackName is required (and we will create utility first)
    if (ENV_TYPES_REQUIRING_UTILITY_CREATE.has(v.type)) {
      if (!v.utilityName || v.utilityName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['utilityName'],
          message: 'Utility Name is required for dev/uat.',
        });
      }
      if (!v.stackName || v.stackName.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['stackName'],
          message: 'Stack Name is required for dev/uat.',
        });
      }
    }
  });

type CreateEnvironmentFormValues = z.infer<typeof createEnvironmentSchema>;

interface CreateEnvironmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateEnvironmentDialog: React.FC<CreateEnvironmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { rc } = useGlobalConfig();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultValues = useMemo<CreateEnvironmentFormValues>(
    () => ({
      name: '',
      awsRegion: AWS_REGION_OPTIONS[0].value,
      stackName: '',
      stateAlphaCode: '',
      serverUrl: '',
      frontend_url: '',
      type: 'dev',
      utilityName: '',
    }),
    []
  );

  const form = useForm<CreateEnvironmentFormValues>({
    resolver: zodResolver(createEnvironmentSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (open) {
      setError(null);
      form.reset(defaultValues);
    }
  }, [open, form, defaultValues, rc]);

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      form.reset(defaultValues);
      onClose();
    }
  };

  const onSubmit = async (values: CreateEnvironmentFormValues) => {
    setSubmitting(true);
    try {
      const utilityName = (values.utilityName || '').trim();
      let resolvedUtilityId: number | null = null;

      // For dev/uat, create utility first so we can use utility_id for env creation
      if (ENV_TYPES_REQUIRING_UTILITY_CREATE.has(values.type)) {
        const createUtilityResp = await PilotApis.createUtility(rc, { name: utilityName });
        if (!createUtilityResp?.error) {
          resolvedUtilityId = extractUtilityIdFromCreateUtilityResponse(createUtilityResp);
        } else {
          // If utility already exists or create failed, try to resolve utility_id by name
          try {
            const listResp = await PilotApis.getUtilities(rc);
            const rows = (listResp?.data?.data || []) as any[];
            const match = rows.find((u: any) => String(u?.name || '').toLowerCase() === utilityName.toLowerCase());
            const id = Number(match?.id);
            if (Number.isFinite(id) && id > 0) resolvedUtilityId = id;
          } catch {
            // ignore; we'll fall back to utilityName below
          }
        }
      }

      const response = await PilotApis.createEnvironment(rc, {
        source: 'deto_admin',
        ...(ENV_TYPES_REQUIRING_UTILITY_CREATE.has(values.type)
          ? resolvedUtilityId != null
            ? { utility_id: resolvedUtilityId }
            : { utilityName }
          : {}),
        name: values.name,
        awsRegion: values.awsRegion,
        serverUrl: values.serverUrl,
        type: values.type,
        frontend_url: values.frontend_url || undefined,
        ...(values.stackName ? { stackName: values.stackName } : {}),
        ...(values.stateAlphaCode ? { stateAlphaCode: values.stateAlphaCode } : {}),
      });
      if (response.error) {
        setError(
          typeof response.error === 'string'
            ? response.error
            : (response.error as any)?.message || 'Failed to create environment'
        );
        return;
      }
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err?.message || 'An error occurred while creating the environment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StudioModal
      open={open}
      onClose={handleClose}
      title="Create New Environment"
      description="Fill in the details for the new environment. Click save when you're done."
      footer={
        <>
          <button
            type="button"
            className={`${styles.button} ${styles.outlineButton}`}
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-environment-form"
            className={`${styles.button} ${styles.primaryButton}`}
            disabled={submitting}
          >
            {submitting ? 'Creating…' : 'Create Environment'}
          </button>
        </>
      }
    >
      <form
        id="create-environment-form"
        className={styles.form}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {error ? <div className={styles.errorBox}>{error}</div> : null}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="create-type">
            Type
          </label>
          <select
            id="create-type"
            className={styles.select}
            disabled={submitting}
            {...form.register('type')}
          >
            {ENVIRONMENT_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {form.formState.errors.type?.message ? (
            <div className={styles.errorText}>{form.formState.errors.type.message}</div>
          ) : null}
        </div>

        {ENV_TYPES_REQUIRING_UTILITY_CREATE.has(form.watch('type')) ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="create-utilityName">
              Utility Name
            </label>
            <input
              id="create-utilityName"
              className={styles.input}
              placeholder="e.g., My Utility"
              disabled={submitting}
              {...form.register('utilityName')}
            />
            {form.formState.errors.utilityName?.message ? (
              <div className={styles.errorText}>{form.formState.errors.utilityName.message}</div>
            ) : null}
            <div className={styles.help}>
              For <strong>dev</strong>/<strong>uat</strong>, we will create this Utility in DB first to resolve a
              utility_id.
            </div>
          </div>
        ) : null}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="create-name">
            Name
          </label>
          <input
            id="create-name"
            className={styles.input}
            placeholder="e.g., Production EU"
            disabled={submitting}
            {...form.register('name')}
          />
          {form.formState.errors.name?.message ? (
            <div className={styles.errorText}>{form.formState.errors.name.message}</div>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="create-awsRegion">
            AWS Region
          </label>
          <select
            id="create-awsRegion"
            className={styles.select}
            disabled={submitting}
            {...form.register('awsRegion')}
          >
            {AWS_REGION_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {form.formState.errors.awsRegion?.message ? (
            <div className={styles.errorText}>{form.formState.errors.awsRegion.message}</div>
          ) : null}
        </div>

        {ENV_TYPES_REQUIRING_UTILITY_CREATE.has(form.watch('type')) ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="create-stackName">
              Stack Name (Queue Suffix)
            </label>
            <input
              id="create-stackName"
              className={styles.input}
              placeholder="e.g., mynewstack"
              disabled={submitting}
              {...form.register('stackName')}
            />
            {form.formState.errors.stackName?.message ? (
              <div className={styles.errorText}>{form.formState.errors.stackName.message}</div>
            ) : null}
            <div className={styles.help}>Required for dev/uat.</div>
          </div>
        ) : null}

        {/* <div className={styles.field}>
          <label className={styles.label} htmlFor="create-stateAlphaCode">
            State Alpha Code
          </label>
          <input
            id="create-stateAlphaCode"
            className={styles.input}
            placeholder="e.g., CA"
            disabled={submitting}
            {...form.register('stateAlphaCode')}
          />
          {form.formState.errors.stateAlphaCode?.message ? (
            <div className={styles.errorText}>{form.formState.errors.stateAlphaCode.message}</div>
          ) : null}
        </div> */}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="create-serverUrl">
            Server URL
          </label>
          <input
            id="create-serverUrl"
            className={styles.input}
            placeholder="https://..."
            disabled={submitting}
            {...form.register('serverUrl')}
          />
          {form.formState.errors.serverUrl?.message ? (
            <div className={styles.errorText}>{form.formState.errors.serverUrl.message}</div>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="create-frontend_url">
            Frontend URL
          </label>
          <input
            id="create-frontend_url"
            className={styles.input}
            placeholder="https://..."
            disabled={submitting}
            {...form.register('frontend_url')}
          />
          {form.formState.errors.frontend_url?.message ? (
            <div className={styles.errorText}>{form.formState.errors.frontend_url.message as string}</div>
          ) : null}
          <div className={styles.help}>Optional.</div>
        </div>
      </form>
    </StudioModal>
  );
};
